import { createElement, lazy, type ComponentType } from "react";
import { ChunkLoadError } from "@/components/ChunkLoadError";
import posthog from "@/lib/posthog";

const RETRY_DELAYS_MS = [500, 1500];
const RELOADED_FOR_KEY = "lazy_chunk_reloaded_for";
const RELOADED_AT_KEY = "lazy_chunk_reloaded_at";
export const RELOAD_COOLDOWN_MS = 10 * 60_000;
const PROBE_TIMEOUT_MS = 8000;
const REFETCH_TIMEOUT_MS = 15000;
const ENTRY_SCRIPT = /<script[^>]+type="module"[^>]+src="([^"]+)"/i;

const STYLESHEET_PRELOAD_ERROR = /unable to preload/i;
const MODULE_LOAD_ERROR =
  /dynamically imported module|importing a module script failed|failed to load module script/i;

export interface ImportRetryOptions {
  delays?: number[];
  reloadOnFailure?: boolean;
  reload?: () => void;
  getDeployedVersion?: () => Promise<string | null>;
}

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return MODULE_LOAD_ERROR.test(message) || STYLESHEET_PRELOAD_ERROR.test(message);
}

function isRetryableInPlace(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return !STYLESHEET_PRELOAD_ERROR.test(message);
}

function wait(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export function entryFromHtml(html: string): string | null {
  return html.match(ENTRY_SCRIPT)?.[1] ?? null;
}

async function fetchText(url: string, cache: RequestCache, timeoutMs: number): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { cache, signal: controller.signal });
    const text = await response.text();
    return response.ok ? text : "";
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function deployedEntry(): Promise<string | null> {
  const html = await fetchText("/index.html", "no-store", PROBE_TIMEOUT_MS);
  return html ? entryFromHtml(html) : null;
}

const failedLinks = new Set<string>();

if (typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLLinkElement) || !target.href) return;
      if (new URL(target.href).origin === window.location.origin) failedLinks.add(target.href);
    },
    true,
  );
}

async function refetchFailedLinks(): Promise<boolean> {
  const results = await Promise.all(
    [...failedLinks].map(async (href) => {
      const answered = (await fetchText(href, "reload", REFETCH_TIMEOUT_MS)) !== null;
      if (answered) failedLinks.delete(href);
      return answered;
    }),
  );
  return !results.includes(false);
}

function recordReloadFor(version: string): boolean {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, version);
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function claimReloadFor(version: string): boolean {
  try {
    const sameVersion = sessionStorage.getItem(RELOADED_FOR_KEY) === version;
    const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT_KEY)) || 0;
    if (sameVersion && Date.now() - reloadedAt < RELOAD_COOLDOWN_MS) return false;
  } catch {
    return false;
  }
  return recordReloadFor(version);
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

async function reachableVersion(getDeployed: () => Promise<string | null>): Promise<string | null> {
  if (isOffline()) return null;
  if (!(await refetchFailedLinks())) return null;
  return getDeployed();
}

async function shouldReload(getDeployed: () => Promise<string | null>): Promise<boolean> {
  const deployed = await reachableVersion(getDeployed);
  if (!deployed) return false;
  return claimReloadFor(deployed);
}

export async function reloadWhenReachable(
  reload: () => void = () => window.location.reload(),
  getDeployed: () => Promise<string | null> = deployedEntry,
): Promise<boolean> {
  const deployed = await reachableVersion(getDeployed);
  if (!deployed) return false;
  recordReloadFor(deployed);
  reload();
  return true;
}

export async function importWithRetry<T>(
  factory: () => Promise<T>,
  options: ImportRetryOptions = {},
): Promise<T> {
  const {
    delays = RETRY_DELAYS_MS,
    reloadOnFailure = true,
    reload = () => window.location.reload(),
    getDeployedVersion = deployedEntry,
  } = options;

  let lastError: unknown;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      lastError = error;
      if (!isRetryableInPlace(error)) break;
      if (attempt < delays.length) await wait(delays[attempt]);
    }
  }

  if (reloadOnFailure && (await shouldReload(getDeployedVersion))) {
    reload();
    return new Promise<T>(() => {});
  }
  throw lastError;
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  options?: ImportRetryOptions,
) {
  return lazy(async () => {
    try {
      return await importWithRetry(factory, options);
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      posthog.captureException(error, { chunk_load_failed: true });
      const Recovery = () => createElement(ChunkLoadError, { onRetry: () => reloadWhenReachable() });
      return { default: Recovery as unknown as T };
    }
  });
}
